import { NewButton, NewButtonSize, NewTextField } from "@bale/ui";
import { digitsToHindi, fromMoneyFormats, toMoneyFormat } from "@bale/utils";
import CardWrapper from "components/commons/cardWrapper/CardWrapper";
import Footer from "components/commons/footer/Footer";
import Header, { NavType } from "components/commons/header/Header";
import Wrapper from "components/commons/wrapper/Wrapper";
import { AppPage, NavigationActions } from "core/navigation/navigation.slice";
import { AdSpot, CampaignType } from "core/orderAd/orderAd.entity";
import { changeCampaignStopValue } from "core/ordersList/ordersList.api";
import { useConfigs } from "hooks/useConfigs";
import {
  GeneralErrors,
  GeneralValidators,
  useInputHandler,
} from "hooks/useInputHandler";
import { strings } from "intl/fa";
import { useAppDispatch, useAppSelector } from "utils/store";
import { formatStrings } from "utils/utils";
import style from "./style.module.scss";
import { toast } from "react-toastify";
import { useEffect, useState } from "react";
import { useMyPanel } from "hooks/useMyPanel";
import { useCampaignPrice } from "hooks/useCampaignPrice";
import { AdType } from "core/orderAd/orderAd.slice";

// TODO: redux
const initView = 1000;
const initClick = 12000;

export const StopCampaignSettings: React.FC<
  React.PropsWithChildren<unknown>
> = () => {
  const dispatch = useAppDispatch();
  const selectedCampaign = useAppSelector(
    (state) => state.ordersList.selectedCampaign
  );
  const ad = selectedCampaign?.ads[0];

  const isView = ad?.type === CampaignType.ViewBased;

  function onClose() {
    dispatch(NavigationActions.setAppPage(AppPage.OrdersList));
  }

  const minStopValue = selectedCampaign?.ads[0].stop_value || 0;

  // TODO: redux
  const stopValueViewCountHandler = useInputHandler({
    initialValue: String(selectedCampaign?.ads[0].stop_value) ?? "",
    beforeChange: fromMoneyFormats,
    numberOnly: true,
    rules: [GeneralValidators.required, GeneralValidators.divisibleBy(1000)],
    errorMessages: {
      [GeneralErrors.divisibleBy]: formatStrings(
        strings.dialog_ad_form.publish_step.divisible_error_view,
        { value: digitsToHindi("1000") }
      ),
      [GeneralErrors.MinimumAmount]: formatStrings(
        strings.dialog_ad_form.publish_step.min_view_count_error,
        {
          count: digitsToHindi(toMoneyFormat(minStopValue.toString())),
        }
      ),
    },
    formatter: toMoneyFormat,
  });

  const stopValueClickCountHandler = useInputHandler({
    initialValue: String(selectedCampaign?.ads[0].stop_value) ?? "",
    beforeChange: fromMoneyFormats,
    numberOnly: true,
    rules: [GeneralValidators.divisibleBy(100)],
    errorMessages: {
      [GeneralErrors.divisibleBy]: formatStrings(
        strings.dialog_ad_form.publish_step.divisible_error_click,
        { value: digitsToHindi("100") }
      ),
      [GeneralErrors.MinimumAmount]: formatStrings(
        strings.dialog_ad_form.publish_step.min_view_count_error,
        {
          count: digitsToHindi(toMoneyFormat(minStopValue.toString())),
        }
      ),
    },
    formatter: toMoneyFormat,
  });

  const [isLoading, setIsLoading] = useState(false);

  const newValue: number = isView
    ? Number(stopValueViewCountHandler.rawValue)
    : Number(stopValueClickCountHandler.rawValue);

  const { credit } = useMyPanel();
  const campaignPrice = useCampaignPrice({
    description: ad?.link ?? ad?.description ?? "",
    adSpot: AdSpot.AdSpotDialog,
    adType: ad?.type === CampaignType.ViewBased ? AdType.view : AdType.click,
  });
  const maxStopValue = Math.floor(credit / campaignPrice);

  function handleSubmit() {
    if (!selectedCampaign) return;
    setIsLoading(true);
    changeCampaignStopValue({ ad_id: ad?.ad_id || "", stop_value: newValue })
      .then(() => {
        dispatch(NavigationActions.setAppPage(AppPage.OrdersList));
      })
      .catch((e) => {
        toast.error(e);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }

  const [stopValueMinError, setStopValueMinError] = useState<string>();
  const [stopValueMaxError, setStopValueMaxError] = useState<string>();
  useEffect(() => {
    if (isView) {
      if (Number(stopValueViewCountHandler.rawValue) > maxStopValue) {
        setStopValueMaxError(
          formatStrings(strings.dialog_ad_form.publish_step.max_stop_error, {
            type: isView ? strings.common.view : strings.common.click,
            count: digitsToHindi(toMoneyFormat(maxStopValue.toString())),
          })
        );
        return;
      } else if (Number(stopValueViewCountHandler.rawValue) < 0) {
        setStopValueMinError(
          strings.dialog_ad_form.publish_step.min_stop_value_error
        );
        return;
      }
    } else {
      if (Number(stopValueClickCountHandler.rawValue) > maxStopValue) {
        setStopValueMaxError(
          formatStrings(strings.dialog_ad_form.publish_step.max_stop_error, {
            type: isView ? strings.common.view : strings.common.click,
            count: digitsToHindi(toMoneyFormat(maxStopValue.toString())),
          })
        );
        return;
      } else if (Number(stopValueClickCountHandler.rawValue) < 0) {
        setStopValueMinError(
          strings.dialog_ad_form.publish_step.min_stop_value_error
        );
        return;
      }
    }
    setStopValueMinError(undefined);
    setStopValueMaxError(undefined);
  }, [
    maxStopValue,
    stopValueClickCountHandler.rawValue,
    stopValueViewCountHandler.rawValue,
  ]);

  useEffect(() => {
    if (stopValueClickCountHandler.rawValue !== "") {
      stopValueClickCountHandler.validate();
    }
    if (stopValueClickCountHandler.rawValue !== "") {
      stopValueClickCountHandler.validate();
    }
  }, [stopValueClickCountHandler.rawValue, stopValueViewCountHandler.rawValue]);

  const isDisable =
    ad?.type === CampaignType.ViewBased
      ? selectedCampaign?.ads[0].stop_value ===
        Number(stopValueViewCountHandler.rawValue)
      : selectedCampaign?.ads[0].stop_value ===
        Number(stopValueClickCountHandler.rawValue);

  return (
    <Wrapper>
      <Header
        onClose={onClose}
        title={strings.stop_campaign.header}
        navType={NavType.both}
      />
      <CardWrapper className={style.Card}>
        <p className={style.Description}>
          {formatStrings(strings.stop_campaign.helper_campaign, {
            type: isView ? strings.viewy : strings.clicky,
          })}
        </p>
        {isView ? (
          <NewTextField
            {...stopValueViewCountHandler}
            placeholder={strings.sponsored_messages.view_count_placeholder}
            label={strings.sponsored_messages.view_count}
            errorText={
              stopValueViewCountHandler.error ||
              stopValueMinError ||
              stopValueMaxError
            }
            direction={stopValueViewCountHandler.rawValue ? "ltr" : "rtl"}
          />
        ) : (
          <NewTextField
            {...stopValueViewCountHandler}
            placeholder={strings.sponsored_messages.view_count_placeholder}
            label={strings.sponsored_messages.view_count}
            errorText={
              stopValueViewCountHandler.error ||
              stopValueMinError ||
              stopValueMaxError
            }
            direction={stopValueViewCountHandler.rawValue ? "ltr" : "rtl"}
          />
        )}
      </CardWrapper>
      <Footer>
        <NewButton
          onClick={handleSubmit}
          style={{ width: "100%", maxWidth: 336, margin: "0 16px" }}
          text={strings.common.button.submit}
          loading={isLoading}
          disabled={isDisable}
          size={NewButtonSize.LARGE}
        />
      </Footer>
    </Wrapper>
  );
};
